/**
 * AdminLogin: staff sign-in for this city's site.
 *
 * Staff accounts belong to one city and are created by an administrator
 * (`python -m app.manage_staff invite`), so there is no sign-up here. A
 * staff session only works on this city's site. Residents sign in at
 * /signin instead; the two kinds of account are separate.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { AuthField, AuthShell, AuthSubmit } from '../components/AuthShell';
import { ApiError } from '../lib/api';
import { isStaffRole } from '../lib/auth';
import { useAuthStore } from '../stores/auth';
import { useTenantStore } from '../stores/tenant';

function errorMessage(error: unknown, t: (key: string) => string): string {
  if (error instanceof ApiError) {
    if (error.code === 'account_locked') return t('auth.accountLocked');
    if (error.code === 'rate_limited') return t('auth.rateLimited');
    if (error.code === 'email_not_verified') return t('auth.notVerified');
    return error.message;
  }
  return error instanceof Error ? error.message : t('auth.signInFailed');
}

export function AdminLogin(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tenant = useTenantStore((s) => s.tenant);
  const user = useAuthStore((s) => s.user);
  const initialized = useAuthStore((s) => s.initialized);
  const staffLogin = useAuthStore((s) => s.staffLogin);
  const logout = useAuthStore((s) => s.logout);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = `${t('auth.staffTitle')} — ${tenant?.name ?? 'CivicLines'}`;
  }, [t, tenant?.name]);

  if (initialized && user && isStaffRole(user.role)) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  async function handleSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await staffLogin(email.trim(), password);
      navigate('/admin/dashboard', { replace: true });
    } catch (err) {
      setError(errorMessage(err, t));
    } finally {
      setBusy(false);
    }
  }

  const signedInResident = initialized && user !== null && !isStaffRole(user.role);

  return (
    <AuthShell
      title={t('auth.staffTitle')}
      subtitle={
        tenant ? t('auth.staffSubtitle', { name: tenant.name }) : t('auth.staffSubtitleGeneric')
      }
    >
      {signedInResident ? (
        <div role="status" className="space-y-3 text-sm text-stone-700">
          <p>{t('auth.staffSignedInAsResident', { email: user.email })}</p>
          <button
            type="button"
            className="rounded-lg bg-tenant-primary px-4 py-2 font-medium text-white hover:bg-tenant-strong"
            onClick={() => void logout()}
          >
            {t('auth.staffSignOutFirst')}
          </button>
        </div>
      ) : (
        <>
          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
            <AuthField
              label={t('auth.workEmail')}
              type="email"
              required
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <AuthField
              label={t('auth.password')}
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {error && (
              <p role="alert" className="text-sm text-red-600">
                {error}
              </p>
            )}
            <AuthSubmit busy={busy} busyLabel={t('auth.signingIn')}>
              {t('auth.signInButton')}
            </AuthSubmit>
          </form>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-sm">
            <p className="text-stone-500">{t('auth.staffNoAccount')}</p>
            <Link to="/forgot-password?staff=1" className="text-stone-500 hover:underline">
              {t('auth.forgotPassword')}
            </Link>
          </div>
        </>
      )}
      <p className="mt-4 border-t border-stone-100 pt-4 text-sm">
        <Link to="/signin" className="font-medium text-tenant-primary hover:underline">
          {t('auth.staffResidentLink')}
        </Link>
      </p>
    </AuthShell>
  );
}
